import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { SetupActivePayment } from '@/components/setup/experience/lib/setupPaymentSummary';
import {
  buildSetupPaymentFlowInput,
  buildSetupProcessorCreateInput,
  isSelectionAlreadyActive,
  isSetupCustomProcessorAvailable,
  resolveSetupLivePayment,
  type SetupCommitPaymentInput,
  type SetupPaymentSelection,
} from '@/components/setup/experience/lib/setupPaymentUtils';
import { useCreatePaymentFlow, useUpdatePaymentFlow } from '@/lib/api/paymentFlows';
import { useCreateProcessor } from '@/lib/api/processors';
import { useUpdateStore } from '@/lib/api/stores';
import { trpc } from '@/lib/api/trpc';

export interface SetupPaymentResult {
  processorId: string;
  paymentFlowId: string;
}

const FULL_WEIGHT = 100;

/**
 * Commits the payment selection idempotently:
 * reuses an existing processor of the same type, updates the bound flow in place
 * (when it is setup-grade), and only rebinds the store when the flow changes.
 */
export function useSetupCommitPayment() {
  const { t } = useTranslation('setup');
  const utils = trpc.useUtils();
  const createProcessor = useCreateProcessor();
  const createPaymentFlow = useCreatePaymentFlow({ silent: true });
  const updatePaymentFlow = useUpdatePaymentFlow({ silent: true });
  const updateStore = useUpdateStore({ silent: true });

  const ensureProcessor = async (
    selection: SetupPaymentSelection,
    storeName: string,
    activeProcessorId: string | null,
  ): Promise<string> => {
    const [processors, tagadapayAccounts] = await Promise.all([
      utils.processors.list.fetch(),
      utils.processors.listTagadapayAccounts.fetch(),
    ]);
    if (selection.kind === 'custom') {
      const processor = processors.find((entry) => entry.id === selection.processorId);
      if (!isSetupCustomProcessorAvailable(processor, tagadapayAccounts)) {
        throw new Error(t('experience.payment.processorUnavailable'));
      }
      return selection.processorId;
    }

    const livePayment =
      selection.type === 'tagadapay-router'
        ? resolveSetupLivePayment(processors, tagadapayAccounts, activeProcessorId)
        : { kind: 'unavailable' as const };
    if (selection.type === 'tagadapay-router' && livePayment.kind === 'existing') {
      return livePayment.processorId;
    }
    if (selection.type === 'tagadapay-router' && livePayment.kind === 'unavailable') {
      throw new Error(t('experience.payment.liveUnavailable'));
    }
    if (selection.type === 'sandbox') {
      const existing = processors.find(
        (processor) => processor.type === 'sandbox' && processor.enabled && !processor.archivedAt,
      );
      if (existing) return existing.id;
    }

    const createInput = buildSetupProcessorCreateInput(selection.type, storeName, livePayment);
    const created = await createProcessor.mutateAsync(createInput);
    const processorId = created[0]?.processor?.id;
    if (!processorId) {
      throw new Error('Failed to create payment processor');
    }

    return processorId;
  };

  const ensureFlow = async (
    processorId: string,
    storeName: string,
    active: SetupActivePayment | null,
  ): Promise<string> => {
    if (active?.isSoloFlow) {
      await updatePaymentFlow.mutateAsync({
        flowId: active.flowId,
        data: {
          processorConfigs: [{ processorId, weight: FULL_WEIGHT, disabled: false, nonStickable: false }],
          fallbackProcessorConfigs: [],
        },
      });
      return active.flowId;
    }

    const flow = await createPaymentFlow.mutateAsync(buildSetupPaymentFlowInput(storeName, processorId));
    if (!flow?.id) {
      throw new Error('Failed to create payment flow');
    }
    return flow.id;
  };

  return useMutation({
    mutationFn: async (input: SetupCommitPaymentInput): Promise<SetupPaymentResult> => {
      const { selection, active } = input;

      // Solo custom bindings must still pass availability checks before taking the no-op path.
      if (
        active &&
        isSelectionAlreadyActive(selection, active) &&
        (selection.kind !== 'custom' || !active.isSoloFlow)
      ) {
        return { processorId: active.processorId, paymentFlowId: active.flowId };
      }

      const processorId = await ensureProcessor(selection, input.storeName, active?.processorId ?? null);
      if (active?.processorId === processorId) {
        return { processorId, paymentFlowId: active.flowId };
      }
      const paymentFlowId = await ensureFlow(processorId, input.storeName, active);

      if (paymentFlowId !== active?.flowId) {
        await updateStore.mutateAsync({
          storeId: input.storeId,
          updatedData: {
            name: input.storeName,
            baseCurrency: input.baseCurrency,
            presentmentCurrencies: [input.baseCurrency],
            chargeCurrencies: [input.baseCurrency],
            selectedPaymentFlowId: paymentFlowId,
          },
        });
      }

      return { processorId, paymentFlowId };
    },
  });
}
